import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, X, Sparkles, Zap, Crown, ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Pricing = () => {
  const { user, getPlanId } = useAuth();
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/plans`);
      setPlans(response.data.plans);
    } catch (error) {
      console.error("Failed to fetch plans:", error);
      toast.error("Error al cargar los planes");
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    if (!user) {
      navigate("/login");
      return;
    }

    if (planId === "free") {
      navigate("/");
      return;
    }

    setCheckoutLoading(planId);

    try {
      const response = await axios.post(`${API}/subscriptions/checkout`, {
        plan_id: planId,
        billing_cycle: billingCycle,
        origin_url: window.location.origin
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Checkout error:", error);
      const message = error.response?.data?.detail || "Error al procesar el pago";
      toast.error(message);
    } finally {
      setCheckoutLoading(null);
    }
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case "free":
        return <Sparkles className="w-6 h-6" />;
      case "basic":
        return <Zap className="w-6 h-6" />;
      case "pro":
        return <Crown className="w-6 h-6" />;
      default:
        return <Sparkles className="w-6 h-6" />;
    }
  };

  const getPlanColor = (planId) => {
    switch (planId) {
      case "free":
        return "from-stone-500 to-stone-600";
      case "basic":
        return "from-orange-500 to-orange-600";
      case "pro":
        return "from-violet-500 to-violet-600";
      default:
        return "from-stone-500 to-stone-600";
    }
  };

  const getPrice = (plan) => {
    if (billingCycle === "monthly") {
      return plan.price_monthly;
    }
    return plan.price_yearly;
  };

  const getMonthlyEquivalent = (plan) => {
    if (billingCycle === "yearly") {
      return (plan.price_yearly / 12).toFixed(2);
    }
    return plan.price_monthly;
  };

  const currentPlanId = getPlanId();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-orange-50/30 to-stone-100">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-stone-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
                <span className="text-white font-bold text-lg">M</span>
              </div>
              <span className="font-semibold text-xl text-stone-800">Minut-IA</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" className="gap-2">
                <ArrowLeft className="w-4 h-4" />
                Volver
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero */}
        <div className="text-center mb-12">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold text-stone-900 mb-4"
          >
            Elige tu plan
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-lg text-stone-600 max-w-2xl mx-auto"
          >
            Transforma tus reuniones en minutas profesionales con inteligencia artificial.
            Selecciona el plan que mejor se adapte a tus necesidades.
          </motion.p>
        </div>

        {/* Billing Toggle */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex justify-center mb-12"
        >
          <div className="bg-white rounded-full p-1 shadow-lg border border-stone-200">
            <div className="flex gap-1">
              <button
                onClick={() => setBillingCycle("monthly")}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                  billingCycle === "monthly"
                    ? "bg-orange-500 text-white shadow-md"
                    : "text-stone-600 hover:text-stone-900"
                }`}
              >
                Mensual
              </button>
              <button
                onClick={() => setBillingCycle("yearly")}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                  billingCycle === "yearly"
                    ? "bg-orange-500 text-white shadow-md"
                    : "text-stone-600 hover:text-stone-900"
                }`}
              >
                Anual
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  billingCycle === "yearly" 
                    ? "bg-white/20 text-white" 
                    : "bg-green-100 text-green-700"
                }`}>
                  -17%
                </span>
              </button>
            </div>
          </div>
        </motion.div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {plans.map((plan, index) => {
            const isCurrentPlan = currentPlanId === plan.id;
            const isPopular = plan.id === "basic";
            const hasPromo = plan.original_price_monthly && plan.id !== "free";
            
            return (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * (index + 1) }}
                className={`relative bg-white rounded-2xl shadow-xl border-2 overflow-hidden ${
                  isPopular ? "border-orange-500 scale-105" : "border-stone-200"
                } ${isCurrentPlan ? "ring-2 ring-green-500" : ""}`}
              >
                {hasPromo && (
                  <div className={`absolute top-0 left-0 right-0 text-white text-center py-1.5 text-xs font-semibold ${
                    plan.id === "pro" 
                      ? "bg-gradient-to-r from-violet-500 to-violet-600"
                      : "bg-gradient-to-r from-orange-500 to-orange-600"
                  }`}>
                    🎉 Promo Lanzamiento Minut-IA
                  </div>
                )}
                
                {isCurrentPlan && (
                  <div className="absolute top-0 right-0 bg-green-500 text-white text-xs px-3 py-1 rounded-bl-lg font-medium">
                    Tu plan actual
                  </div>
                )}

                <div className={`p-6 ${hasPromo ? "pt-10" : ""}`}>
                  {/* Plan Header */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getPlanColor(plan.id)} flex items-center justify-center text-white`}>
                      {getPlanIcon(plan.id)}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-stone-900">{plan.name}</h3>
                      <p className="text-sm text-stone-500">{plan.description}</p>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="mb-6">
                    {plan.original_price_monthly && plan.id !== "free" ? (
                      <>
                        {/* Original price (crossed out) */}
                        <div className="flex items-baseline gap-2 mb-1">
                          <span className="text-lg font-medium text-stone-400 line-through">
                            ${billingCycle === "monthly" ? plan.original_price_monthly.toFixed(2) : plan.original_price_yearly.toFixed(2)}
                          </span>
                          <span className="bg-red-100 text-red-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                            -{Math.round((1 - getPrice(plan) / (billingCycle === "monthly" ? plan.original_price_monthly : plan.original_price_yearly)) * 100)}%
                          </span>
                        </div>
                        {/* Promo price */}
                        <div className="flex items-baseline gap-1">
                          <span className="text-4xl font-bold text-orange-600">
                            ${getPrice(plan).toFixed(2)}
                          </span>
                          <span className="text-stone-500">
                            /{billingCycle === "monthly" ? "mes" : "año"}
                          </span>
                        </div>
                      </>
                    ) : (
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-bold text-stone-900">
                          ${getPrice(plan).toFixed(2)}
                        </span>
                        <span className="text-stone-500">
                          /{billingCycle === "monthly" ? "mes" : "año"}
                        </span>
                      </div>
                    )}
                    {billingCycle === "yearly" && plan.price_yearly > 0 && (
                      <p className="text-sm text-stone-500 mt-1">
                        ${getMonthlyEquivalent(plan)}/mes facturado anualmente
                      </p>
                    )}
                  </div>

                  {/* CTA Button */}
                  <Button
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={isCurrentPlan || checkoutLoading === plan.id}
                    className={`w-full mb-6 ${
                      plan.id === "pro" 
                        ? "bg-gradient-to-r from-violet-500 to-violet-600 hover:from-violet-600 hover:to-violet-700"
                        : plan.id === "basic"
                        ? "bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
                        : ""
                    }`}
                    variant={plan.id === "free" ? "outline" : "default"}
                  >
                    {checkoutLoading === plan.id ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Procesando...</>
                    ) : isCurrentPlan ? (
                      "Plan actual"
                    ) : plan.id === "free" ? (
                      "Comenzar gratis"
                    ) : (
                      "Suscribirse"
                    )}
                  </Button>

                  {/* Features */}
                  <div className="space-y-3">
                    <p className="text-sm font-medium text-stone-700">Incluye:</p>
                    {plan.features.map((feature, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-stone-600">{feature}</span>
                      </div>
                    ))}
                    {plan.limitations?.map((limitation, i) => (
                      <div key={`lim-${i}`} className="flex items-start gap-2">
                        <X className="w-5 h-5 text-stone-300 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-stone-400">{limitation}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* FAQ or Additional Info */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-16 text-center"
        >
          <p className="text-sm text-stone-400">
            Todos los pagos son procesados de forma segura con Stripe.
            Puedes cancelar tu suscripción en cualquier momento.
          </p>
        </motion.div>
      </main>
    </div>
  );
};

export default Pricing;
